import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { YazturLogo } from "@/components/Brand";
import NotificationBell from "@/components/NotificationBell";
import PettyCashDrawer from "@/components/PettyCashDrawer";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  LayoutDashboard, ClipboardList, Users, Truck, Coins, Trophy, ClipboardCheck,
  Settings as SettingsIcon, LogOut, UserCog, FilePlus2, X, Bus, GitCompare, Upload,
  Mail, User as UserIcon, IdCard, CalendarDays, AlertTriangle, Award, Wrench,
  Wallet, Menu, Plus, ChevronRight, ChevronDown, Repeat2, AlertOctagon,
} from "lucide-react";

const APP_VERSION = "v2.4.0";

const ROLE_LABELS = { admin: "Yönetici", data_entry: "Veri Giriş", viewer: "Görüntüleyici" };

/* ─── Menu structure ──────────────────────────────────────────── */
const GROUP_COLORS = {
  shop:   "#FF7A1A",
  people: "#2EE6A8",
  field:  "#8B9CFF",
  bonus:  "#F5C518",
  admin:  "#9BA3AE",
};

const SECTIONS = [
  {
    key: "shop", title: "ATÖLYE & BAKIM", color: GROUP_COLORS.shop, icon: Wrench,
    items: [
      { to: "/is-emirleri",    label: "İş Emirleri",              icon: ClipboardList, roles: ["admin","data_entry","viewer"] },
      { to: "/tekrar-arizalar",label: "Tekrar Arızalar",         icon: Repeat2, roles: ["admin","data_entry","viewer"] },
      { to: "/araclar",        label: "Filo (Araçlar)",           icon: Bus, roles: ["admin","data_entry","viewer"] },
      { to: "/kronik",         label: "Kronik Arıza",             icon: AlertOctagon, roles: ["admin","data_entry","viewer"] },
      { to: "/karsilastirma",  label: "Araç / Hat Karşılaştırma", icon: GitCompare, roles: ["admin","data_entry","viewer"] },
    ],
  },
  {
    key: "people", title: "PERSONEL & ŞOFÖR", color: GROUP_COLORS.people, icon: IdCard,
    items: [
      { to: "/soforler",       label: "Personel",         icon: IdCard, roles: ["admin","data_entry"], badge: "docs" },
      { to: "/sofor-disiplin", label: "Disiplin & Prim",  icon: Award, roles: ["admin","data_entry","viewer"] },
      { to: "/kaza-raporlari", label: "Kaza Raporları",   icon: AlertTriangle, roles: ["admin","data_entry","viewer"] },
      { to: "/vardiya",        label: "Vardiya Çizelgesi",icon: CalendarDays, roles: ["admin","data_entry","viewer"] },
    ],
  },
  {
    key: "field", title: "SAHA", color: GROUP_COLORS.field, icon: ClipboardCheck,
    items: [
      { to: "/saha-gorevler",    label: "Günlük Görevler",  icon: ClipboardCheck, roles: ["admin","data_entry"] },
      { to: "/saha-sorumlulari", label: "Saha Primi",       icon: Trophy, roles: ["admin"] },
    ],
  },
  {
    key: "bonus", title: "HAKEDİŞ", color: GROUP_COLORS.bonus, icon: Trophy,
    items: [
      { to: "/ustalar",     label: "Ustalar",       icon: Users, roles: ["admin","data_entry","viewer"] },
      { to: "/prim-hakedis",label: "Prim & Karne",  icon: Trophy, roles: ["admin"] },
    ],
  },
  {
    key: "ops", title: "OPERASYON", color: "#22C55E", icon: Wallet,
    items: [
      { to: "/kasa",   label: "Kasa",                 icon: Wallet,       roles: ["admin","data_entry","viewer"] },
      { to: "/notlar", label: "Notlar & İhtiyaçlar",  icon: ClipboardList, roles: ["admin","data_entry","viewer"] },
    ],
  },
  {
    key: "admin", title: "YÖNETİM", color: GROUP_COLORS.admin, icon: SettingsIcon,
    items: [
      { to: "/dis-servis",   label: "Dış Servis Kayıtları", icon: Truck,   roles: ["admin","data_entry","viewer"] },
      { to: "/kullanicilar", label: "Sistem Kullanıcıları", icon: UserCog, roles: ["admin"] },
      { to: "/personel",     label: "Bildirim E-postaları", icon: Mail,    roles: ["admin"] },
      { to: "/ayarlar",      label: "Ayarlar",              icon: SettingsIcon, roles: ["admin"] },
    ],
  },
];

// Legacy — no longer used; menü artık roles[] filtreliyor
const DATA_ENTRY_NAV = [];

function filterByRole(items, role, username) {
  if (!role) return [];
  return items.filter((it) => {
    if (!Array.isArray(it.roles) || !it.roles.includes(role)) return false;
    // Update 29.12 — superAdminOnly: sadece username === "admin"
    if (it.superAdminOnly && username !== "admin") return false;
    return true;
  });
}

/* ─── Group cell (icon in tinted square) ─────────────────────── */
function GroupIconTile({ Icon, color, size = 28, active = false }) {
  return (
    <span
      className="inline-flex items-center justify-center shrink-0"
      style={{
        width: size, height: size, borderRadius: 8,
        background: active ? color : `${color}26`,
        color: active ? "#14161A" : color,
        transition: "background .15s ease, color .15s ease",
      }}
    >
      <Icon className="w-[16px] h-[16px]" strokeWidth={2} />
    </span>
  );
}

/* ─── Nav item ───────────────────────────────────────────────── */
function NavItem({ item, groupColor, badgeValue, onClick }) {
  const IconComp = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      data-testid={`nav-${item.to.replace("/", "")}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 h-9 pl-2 pr-3 rounded-[10px] text-[13px] font-medium transition outline-none ${
          isActive
            ? "text-white"
            : "text-[color:var(--y-text-2)] hover:text-white"
        }`
      }
      style={({ isActive }) => (isActive
        ? { background: "var(--y-accent-tint)" }
        : {})
      }
    >
      {({ isActive }) => (
        <>
          <GroupIconTile Icon={IconComp} color={groupColor} active={isActive} />
          <span className="truncate flex-1">{item.label}</span>
          {badgeValue > 0 && (
            <span
              className="mono"
              style={{
                background: item.badge === "docs" ? "var(--y-warning)" : "var(--y-muted)",
                color: "var(--y-accent-fg)",
                fontSize: 11, fontWeight: 700,
                padding: "1px 6px", borderRadius: 6, minWidth: 20, textAlign: "center",
              }}
              data-testid={`nav-badge-${item.badge}`}
            >
              {badgeValue > 99 ? "99+" : badgeValue}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Sidebar section (collapsible) ──────────────────────────── */
function Section({ section, role, username, driverBadge, docsBadge, openMap, toggleGroup, closeMobile }) {
  const items = filterByRole(section.items, role, username);
  if (!items.length) return null;
  const isOpen = openMap[section.key] !== false; // default open
  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => toggleGroup(section.key)}
        data-testid={`sidebar-group-${section.key}`}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[color:var(--y-surface-2)] transition"
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: section.color }}
        />
        <span
          className="flex-1 text-left"
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            color: "var(--y-label)", textTransform: "uppercase",
          }}
        >
          {section.title}
        </span>
        {!isOpen && (
          <span className="mono" style={{ fontSize: 10, color: "var(--y-muted)" }}>
            {items.length}
          </span>
        )}
        {isOpen ? (
          <ChevronDown className="w-3.5 h-3.5" style={{ color: "var(--y-muted)" }} />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--y-muted)" }} />
        )}
      </button>
      {isOpen && (
        <div className="space-y-0.5 mt-1">
          {items.map((it) => {
            let val = 0;
            if (it.badge === "docs") val = docsBadge;
            return (
              <NavItem
                key={it.to}
                item={it}
                groupColor={section.color}
                badgeValue={val}
                onClick={closeMobile}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Mobile bottom navigation ───────────────────────────────── */
/* Update 29.16 — Tüm roller için TEK TİP alt bar:
   [Panel] · [Rol kısayolu] · [+] · [Rol kısayolu] · [Menü]
   Önceki sürümde data_entry'de "Menü" butonu yoktu; non-admin kullanıcılar
   mobilde navigasyon yapamıyordu. */
function MobileBottomNav({ role, onOpenMenu, onOpenQuick }) {
  // Rol bazlı sol kısayol (Panel'in yanı) ve sağ kısayol (Menü'nün yanı)
  const leftShortcut = role === "data_entry"
    ? { to: "/veri-giris", icon: FilePlus2, label: "İş Emri" }
    : { to: "/is-emirleri", icon: ClipboardList, label: "İş Emri" };
  const rightShortcut = role === "data_entry"
    ? { to: "/notlar", icon: ClipboardList, label: "Notlar" }
    : { to: "/kasa", icon: Wallet, label: "Kasa" };

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-30 pb-safe"
      style={{ background: "var(--y-sidebar)", borderTop: "1px solid var(--y-border)" }}
    >
      <div className="h-[76px] flex items-end justify-around px-2 pt-2 relative">
        <MobileTab to="/dashboard" Icon={LayoutDashboard} label="Panel" />
        <MobileTab to={leftShortcut.to} Icon={leftShortcut.icon} label={leftShortcut.label} />
        {/* Centered elevated CTA — Hızlı İşlem menüsü açar */}
        <button
          onClick={onOpenQuick}
          data-testid="mobile-quick-btn"
          className="absolute left-1/2 -translate-x-1/2 -top-4 w-14 h-14 rounded-full flex items-center justify-center"
          style={{ background: "var(--y-accent)", color: "var(--y-accent-fg)", boxShadow: "0 8px 24px rgba(255,122,26,0.35)" }}
          aria-label="Hızlı işlem"
        >
          <Plus className="w-6 h-6" strokeWidth={2.6} />
        </button>
        <div className="w-14" />
        <MobileTab to={rightShortcut.to} Icon={rightShortcut.icon} label={rightShortcut.label} />
        <button
          onClick={onOpenMenu}
          data-testid="mobile-menu-btn"
          className="flex flex-col items-center justify-center gap-0.5 h-full px-3 text-[color:var(--y-muted)]"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px]">Menü</span>
        </button>
      </div>
    </nav>
  );
}

/* ─── Mobile Quick Action Sheet (Hızlı İşlem) ───────────────── */
function QuickActionSheet({ open, onClose, role, navigate }) {
  if (!open) return null;
  const canCreateWO = role === "admin" || role === "data_entry";
  return (
    <>
      <div onClick={onClose} className="lg:hidden fixed inset-0 bg-black/60 z-40" data-testid="quick-action-overlay" />
      <div
        data-testid="quick-action-sheet"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl p-4 pb-8"
        style={{ background: "var(--y-sidebar)", borderTop: "1px solid var(--y-border)" }}
      >
        <div className="text-center text-[11px] uppercase tracking-widest text-[color:var(--y-muted)] mb-3">Hızlı İşlem</div>
        <div className="space-y-2">
          <button
            onClick={() => { onClose(); navigate("/kasa"); }}
            data-testid="quick-action-kasa"
            className="w-full h-14 flex items-center gap-3 px-4 rounded-[12px]"
            style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)", color: "var(--y-text)" }}
          >
            <span className="w-10 h-10 rounded-md bg-emerald-500/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </span>
            <div className="text-left">
              <div className="font-semibold text-sm">Masraf Ekle</div>
              <div className="text-[11px] text-[color:var(--y-muted)]">Cepten yapılan harcama, fişli/fişsiz</div>
            </div>
          </button>
          <button
            onClick={() => { onClose(); navigate("/notlar"); }}
            data-testid="quick-action-not"
            className="w-full h-14 flex items-center gap-3 px-4 rounded-[12px]"
            style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)", color: "var(--y-text)" }}
          >
            <span className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-blue-300" />
            </span>
            <div className="text-left">
              <div className="font-semibold text-sm">Not / İhtiyaç Ekle</div>
              <div className="text-[11px] text-[color:var(--y-muted)]">Öncelikli not, opsiyonel fotoğraf</div>
            </div>
          </button>
          {canCreateWO && (
            <button
              onClick={() => { onClose(); navigate(role === "data_entry" ? "/veri-giris" : "/yeni-is-emri"); }}
              data-testid="quick-action-wo"
              className="w-full h-14 flex items-center gap-3 px-4 rounded-[12px]"
              style={{ background: "var(--y-accent)", color: "var(--y-accent-fg)" }}
            >
              <span className="w-10 h-10 rounded-md bg-black/20 flex items-center justify-center">
                <FilePlus2 className="w-5 h-5" />
              </span>
              <div className="text-left">
                <div className="font-semibold text-sm">Yeni İş Emri</div>
                <div className="text-[11px] opacity-80">Bakım/onarım kaydı aç</div>
              </div>
            </button>
          )}
        </div>
        <button onClick={onClose} className="mt-4 w-full h-10 rounded-[10px] text-sm"
                style={{ color: "var(--y-muted)", border: "1px solid var(--y-border)" }}>
          Vazgeç
        </button>
      </div>
    </>
  );
}
function MobileTab({ to, Icon, label }) {
  return (
    <NavLink
      to={to}
      data-testid={`mobile-tab-${to.replace("/", "")}`}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 h-full px-3 ${
          isActive ? "text-[color:var(--y-accent)]" : "text-[color:var(--y-muted)]"
        }`
      }
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px]">{label}</span>
    </NavLink>
  );
}

/* ─── Fleet status card ──────────────────────────────────────── */
function FleetStatusCard({ status, loading }) {
  if (loading || !status) return null;
  const { total, ready, warning, critical } = status;
  if (!total) return null;
  const seg = (v) => `${(v / total) * 100}%`;
  return (
    <div
      className="mx-3 mb-3 px-3 py-3 rounded-[10px]"
      style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)" }}
      data-testid="fleet-status-card"
    >
      <div className="flex items-baseline justify-between mb-2">
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "var(--y-label)" }}>
          FİLO DURUMU
        </span>
        <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: "var(--y-text)" }}>
          {total}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden flex" style={{ background: "var(--y-surface-2)" }}>
        <div style={{ width: seg(ready),    background: "var(--y-success)" }} />
        <div style={{ width: seg(warning),  background: "var(--y-accent)"  }} />
        <div style={{ width: seg(critical), background: "var(--y-danger)"  }} />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]" style={{ color: "var(--y-muted)" }}>
        <span className="flex items-center gap-1"><Dot c="var(--y-success)" />Sefere hazır</span>
        <span className="flex items-center gap-1"><Dot c="var(--y-accent)" />Uyarı</span>
        <span className="flex items-center gap-1"><Dot c="var(--y-danger)" />Kritik</span>
      </div>
    </div>
  );
}
function Dot({ c }) {
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />;
}

/* ─── AppShell ───────────────────────────────────────────────── */
export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [docsBadge, setDocsBadge] = useState(0);
  const [fleet, setFleet] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);

  const [openMap, setOpenMap] = useState(() => {
    try {
      const raw = localStorage.getItem("yaztur.nav.groups");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleGroup = (k) => {
    setOpenMap((prev) => {
      const next = { ...prev, [k]: prev[k] === false ? true : false };
      try { localStorage.setItem("yaztur.nav.groups", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (!user || !(user.role === "admin" || user.role === "data_entry")) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/drivers/document-warnings");
        if (!cancelled) setDocsBadge(data?.count || 0);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "data_entry") return;
    let cancelled = false;
    const load = async () => {
      setFleetLoading(true);
      try {
        const [{ data: vehicles }, { data: settings }] = await Promise.all([
          api.get("/analytics/vehicles"),
          api.get("/settings").catch(() => ({ data: {} })),
        ]);
        const threshold = settings?.critical_repeat_threshold || 3;
        const total = vehicles.length;
        const critical = vehicles.filter((v) => (v.repeat_count || 0) >= threshold).length;
        const warning  = vehicles.filter((v) => (v.repeat_count || 0) > 0 && (v.repeat_count || 0) < threshold).length;
        const ready    = Math.max(total - critical - warning, 0);
        if (!cancelled) setFleet({ total, ready, warning, critical });
      } catch { /* silent */ }
      finally { if (!cancelled) setFleetLoading(false); }
    };
    load();
    const t = setInterval(load, 90000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "";
  const initial = user?.name?.[0]?.toUpperCase() || "U";
  const showBell = user?.role === "admin" || user?.role === "viewer";
  const isDataEntry = user?.role === "data_entry";
  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex" style={{ background: "var(--y-bg)" }}>
      {/* ─── Update 29.4 — Görüntüleme modu şeridi ─── */}
      {user?.view_as && (
        <div
          data-testid="view-as-banner"
          style={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
            background: "#EA580C", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 16px", fontSize: 13, fontWeight: 600,
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
        >
          <span>
            👁 <b>{user.name}</b> ({user.role}) olarak görüntülüyorsunuz · Değişiklik yapılamaz
            <span style={{ marginLeft: 12, opacity: 0.85, fontWeight: 400 }}>
              Başlatan: {user.view_as.actor_name || user.view_as.actor_username}
            </span>
          </span>
          <button
            data-testid="view-as-stop-btn"
            onClick={async () => { await logout(); navigate("/dashboard"); window.location.reload(); }}
            style={{
              background: "rgba(0,0,0,0.25)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8,
              padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            Çık
          </button>
        </div>
      )}

      {/* ─────── Desktop sidebar ─────── */}
      {(
        <aside
          data-testid="app-sidebar"
          className={`${mobileMenuOpen ? "flex" : "hidden"} lg:flex flex-col fixed lg:sticky top-0 inset-y-0 left-0 z-40 h-screen`}
          style={{
            width: 272,
            background: "var(--y-sidebar)",
            borderRight: "1px solid var(--y-border)",
          }}
        >
          {/* Logo */}
          <div className="h-16 flex items-center px-4 shrink-0">
            <YazturLogo size={36} />
            <button
              onClick={closeMobile}
              className="lg:hidden ml-auto p-1.5 rounded hover:bg-[color:var(--y-surface-2)]"
              data-testid="sidebar-close-mobile"
              aria-label="Menüyü kapat"
            >
              <X className="w-4 h-4" style={{ color: "var(--y-muted)" }} />
            </button>
          </div>

          {/* Primary CTA + Dashboard */}
          <div className="px-3 pb-2 shrink-0 space-y-2">
            {(user?.role === "admin" || user?.role === "data_entry") && (
              <NavLink
                to="/yeni-is-emri"
                onClick={closeMobile}
                data-testid="sidebar-new-order-cta"
                className="w-full h-11 flex items-center justify-center gap-2 rounded-[10px] font-semibold"
                style={{
                  background: "var(--y-accent)", color: "var(--y-accent-fg)",
                  fontFamily: "'Barlow Semi Condensed', sans-serif", fontSize: 15, letterSpacing: "0.01em",
                }}
              >
                <Plus className="w-4 h-4" strokeWidth={2.6} />
                Yeni İş Emri
              </NavLink>
            )}
            <div className="grid grid-cols-2 gap-2">
              <NavLink
                to="/kasa"
                onClick={closeMobile}
                data-testid="sidebar-quick-kasa"
                className="h-9 flex items-center justify-center gap-1.5 rounded-[8px] text-[12px] font-medium border"
                style={{ borderColor: "var(--y-border)", color: "var(--y-text-2)", background: "var(--y-surface)" }}
              >
                <Wallet className="w-3.5 h-3.5" /> Masraf Ekle
              </NavLink>
              <NavLink
                to="/notlar"
                onClick={closeMobile}
                data-testid="sidebar-quick-not"
                className="h-9 flex items-center justify-center gap-1.5 rounded-[8px] text-[12px] font-medium border"
                style={{ borderColor: "var(--y-border)", color: "var(--y-text-2)", background: "var(--y-surface)" }}
              >
                <ClipboardList className="w-3.5 h-3.5" /> Not Ekle
              </NavLink>
            </div>
          </div>
          <div className="px-3 mb-2 shrink-0">
            <NavLink
              to="/dashboard"
              onClick={closeMobile}
              data-testid="nav-dashboard"
              className={({ isActive }) =>
                `flex items-center gap-3 h-9 pl-2 pr-3 rounded-[10px] text-[13px] font-medium transition ${
                  isActive ? "text-white" : "text-[color:var(--y-text-2)] hover:text-white"
                }`
              }
              style={({ isActive }) => (isActive ? { background: "var(--y-accent-tint)" } : {})}
            >
              {({ isActive }) => (
                <>
                  <GroupIconTile Icon={LayoutDashboard} color="var(--y-accent)" active={isActive} />
                  <span className="flex-1">Kontrol Paneli</span>
                </>
              )}
            </NavLink>
          </div>

          {/* Groups */}
          <nav className="flex-1 overflow-y-auto px-3 pb-3">
            {SECTIONS.map((s) => (
              <Section
                key={s.key}
                section={s}
                role={user?.role}
                username={user?.username}
                docsBadge={docsBadge}
                openMap={openMap}
                toggleGroup={toggleGroup}
                closeMobile={closeMobile}
              />
            ))}
          </nav>

          {/* Fleet status */}
          <FleetStatusCard status={fleet} loading={fleetLoading} />

          {/* Version footer */}
          <div
            className="px-4 py-2 shrink-0 flex items-center justify-between mono"
            style={{ fontSize: 10, color: "var(--y-label)", borderTop: "1px solid var(--y-border)", letterSpacing: "0.08em" }}
          >
            <span>YAZTUR · BURSA</span>
            <span>{APP_VERSION}</span>
          </div>
        </aside>
      )}

      {/* ─────── Main column ─────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 flex items-center gap-3 px-4 lg:px-8 pt-safe"
          style={{
            height: 72,
            background: "var(--y-bg)",
            borderBottom: "1px solid var(--y-border)",
          }}
        >
          {/* Mobile logo (replaces old hamburger) */}
          <div className="lg:hidden">
            <YazturLogo size={32} />
          </div>

          {/* Kasa button (only for admin/viewer, desktop only) */}
          {!isDataEntry && (
            <button
              data-testid="topbar-kasa-btn"
              onClick={() => window.dispatchEvent(new CustomEvent("yaztur:open-petty-cash"))}
              className="hidden md:inline-flex items-center gap-2 h-10 px-3 rounded-[10px] text-sm font-medium ml-auto"
              style={{
                background: "var(--y-surface)",
                border: "1px solid var(--y-border)",
                color: "var(--y-text-2)",
              }}
              title="Kasa Çekmecesi"
            >
              <Wallet className="w-4 h-4" />
              <span>Kasa</span>
            </button>
          )}

          <div className="lg:hidden ml-auto" />
          {showBell && <NotificationBell />}

          {/* Account */}
          <div className="relative">
            <button
              data-testid="topbar-account-btn"
              onClick={() => setAccountOpen((v) => !v)}
              className="flex items-center gap-2 h-10 pl-2 pr-3 rounded-[10px]"
              style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)" }}
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full font-semibold"
                style={{ background: "var(--y-accent)", color: "var(--y-accent-fg)", fontSize: 12 }}
              >
                {initial}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-tight">
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--y-text)" }}>
                  {user?.name || "Kullanıcı"}
                </span>
                <span style={{ fontSize: 11, color: "var(--y-muted)" }}>{roleLabel}</span>
              </span>
            </button>
            {accountOpen && (
              <div
                data-testid="account-menu"
                className="absolute right-0 mt-2 w-48 rounded-[10px] overflow-hidden z-50"
                style={{ background: "var(--y-surface)", border: "1px solid var(--y-border)" }}
                onMouseLeave={() => setAccountOpen(false)}
              >
                <button
                  data-testid="account-profile"
                  onClick={() => { setAccountOpen(false); navigate("/profil"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[color:var(--y-surface-2)]"
                  style={{ color: "var(--y-text-2)" }}
                >
                  <UserIcon className="w-4 h-4" /> Profilim
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={doLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[color:var(--y-surface-2)]"
                  style={{ color: "var(--y-danger)" }}
                >
                  <LogOut className="w-4 h-4" /> Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main */}
        <main
          className="flex-1 p-4 lg:p-8"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom) + 120px)",
            background: "var(--y-bg)",
          }}
          data-testid="app-main"
        >
          <ErrorBoundary key={typeof window !== "undefined" ? window.location.pathname : "root"}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile menu is now driven by bottom nav "Menü" button — no top-bar hamburger */}

      {/* Mobile overlay — Update 29.16: data_entry dahil tüm rollerde göster. */}
      {mobileMenuOpen && (
        <div
          onClick={closeMobile}
          className="lg:hidden fixed inset-0 bg-black/60 z-30"
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile bottom nav */}
      <MobileBottomNav role={user?.role} onOpenMenu={() => setMobileMenuOpen(true)} onOpenQuick={() => setQuickOpen(true)} />

      {/* Quick action sheet (mobile "+") */}
      <QuickActionSheet open={quickOpen} onClose={() => setQuickOpen(false)} role={user?.role} navigate={navigate} />

      {/* Petty cash drawer (opened via top-bar button / event) */}
      {!isDataEntry && <PettyCashDrawer />}
    </div>
  );
}
